import { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Download, FileText, Printer } from "lucide-react";
import { istNow, ymd, useIstDay } from "../lib/dates";

const iso = (d) => ymd(d);

function presetRange(preset) {
  const now = istNow();
  const t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === "today") return { from: iso(t), to: iso(t) };
  if (preset === "yesterday") { const y = new Date(t); y.setDate(t.getDate() - 1); return { from: iso(y), to: iso(y) }; }
  if (preset === "week") {
    const day = (t.getDay() + 6) % 7;
    const mon = new Date(t); mon.setDate(t.getDate() - day);
    return { from: iso(mon), to: iso(t) };
  }
  if (preset === "month") {
    const first = new Date(t.getFullYear(), t.getMonth(), 1);
    return { from: iso(first), to: iso(t) };
  }
  return null;
}

export default function StaffSummaryReport() {
  const { branchParam } = useBranch();
  const [preset, setPreset] = useState("month");
  const [custom, setCustom] = useState({ from: iso(istNow()), to: iso(istNow()) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const istDay = useIstDay();
  const range = useMemo(() => (preset === "custom" ? custom : presetRange(preset)), [preset, custom, istDay]); // istDay: IST midnight rollover

  useEffect(() => {
    if (!range) return;
    setLoading(true);
    api.get("/incentives/staff-summary", { params: { from_date: range.from, to_date: range.to, branch_id: branchParam } })
      .then((r) => setData(r.data)).catch(() => toast.error("Failed to load report"))
      .finally(() => setLoading(false));
  }, [range, branchParam]);

  const t = data?.totals;

  const cards = t ? [
    { label: "Total Business", value: rupee(t.total_business) },
    { label: "Total Bills", value: t.total_bills, plain: true },
    { label: "Total Clients", value: t.total_clients, plain: true },
    { label: "Total GST", value: rupee(t.total_gst) },
    { label: "Net Business After GST", value: rupee(t.net_business) },
    { label: "Total Product Sales", value: rupee(t.total_product_sales) },
    { label: "Total Incentive", value: rupee(t.total_incentive), highlight: true },
  ] : [];

  const grossText = (r) => {
    let s = String(r.gross_service);
    if (r.branch_breakdown?.length) s += ` (${r.branch_breakdown.filter((b) => b.gross_service).map((b) => `${b.short_name} ${b.gross_service}`).join("; ")})`;
    if (r.is_home_branch === false) s += ` [Incentive at ${r.home_branch_short}]`;
    return `"${s}"`;
  };

  const exportCsv = () => {
    if (!data) return;
    const head = ["Staff", "Services Count", "Gross Business", "GST", "Net Business After GST",
      "Incentive Slab", "Incentive %", "Service Incentive", "Product Sales", "Product Incentive", "Total Incentive"];
    const lines = [head.join(",")];
    data.rows.forEach((r) => lines.push([r.staff_name, r.services_count, grossText(r), r.gst, r.net_service,
      `"${r.slab_label}"`, r.slab_percent, r.service_incentive, r.product_sales, r.product_incentive, r.total_incentive].join(",")));
    lines.push(["TOTAL", "", t.total_gross_service, t.total_gst, t.net_business,
      "", "", t.total_service_incentive, t.total_product_sales, t.total_product_incentive, t.total_incentive].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `staff_incentive_${range.from}_${range.to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    if (!data) return;
    const token = localStorage.getItem("ds_token");
    const qs = new URLSearchParams({ from_date: range.from, to_date: range.to });
    if (branchParam) qs.append("branch_id", branchParam);
    fetch(`${API}/incentives/staff-summary/pdf?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 30000); })
      .catch(() => toast.error("PDF export failed"));
  };

  return (
    <div className="space-y-5" data-testid="staff-summary-report">
      <Card className="p-4 bg-card border-border">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex gap-2">
            {[["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["month", "This Month"], ["custom", "Custom"]].map(([k, lbl]) => (
              <Button key={k} size="sm" variant={preset === k ? "default" : "outline"}
                onClick={() => setPreset(k)} data-testid={`summary-preset-${k}`}
                className={`rounded-sm ${preset === k ? "bg-primary text-primary-foreground" : ""}`}>{lbl}</Button>
            ))}
          </div>
          {preset === "custom" && (
            <div className="flex items-end gap-2">
              <div><label className="text-xs text-muted-foreground">From</label>
                <Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} data-testid="summary-from" className="bg-secondary mt-1 w-40" /></div>
              <div><label className="text-xs text-muted-foreground">To</label>
                <Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} data-testid="summary-to" className="bg-secondary mt-1 w-40" /></div>
            </div>
          )}
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="outline" className="rounded-sm" onClick={exportCsv} disabled={!data} data-testid="summary-export-csv"><Download size={14} className="mr-1" /> CSV</Button>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={exportPdf} disabled={!data} data-testid="summary-export-pdf"><FileText size={14} className="mr-1" /> PDF</Button>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => window.print()} disabled={!data} data-testid="summary-print"><Printer size={14} className="mr-1" /> Print</Button>
          </div>
        </div>
      </Card>

      <div id="staff-summary-print" className="space-y-5">
        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {cards.map((c, i) => (
            <Card key={i} className="p-4 bg-card border-border">
              <p className="text-xs text-muted-foreground">{c.label}</p>
              <p className={`font-display mt-1 ${c.plain ? "text-3xl" : "text-2xl"} ${c.highlight ? "text-primary" : ""}`} data-testid={`summary-card-${i}`}>{c.value}</p>
            </Card>
          ))}
        </div>

        <p className="text-xs text-muted-foreground" data-testid="summary-rule-note">
          Business shown is what each staff performed in {data?.branch_name || "the selected branch"}. Incentive is calculated on each staff's combined Net Business After GST across all branches and allocated once, in their Home Branch.
        </p>

        <Card className="bg-card border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead>Staff Name</TableHead>
                <TableHead className="text-right">Services Count</TableHead>
                <TableHead className="text-right">Gross Business</TableHead>
                <TableHead className="text-right">GST</TableHead>
                <TableHead className="text-right">Net Business After GST</TableHead>
                <TableHead className="text-right">Incentive Slab</TableHead>
                <TableHead className="text-right">Incentive %</TableHead>
                <TableHead className="text-right">Service Incentive</TableHead>
                <TableHead className="text-right">Product Sales</TableHead>
                <TableHead className="text-right">Product Incentive</TableHead>
                <TableHead className="text-right">Total Incentive</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.rows.map((r) => (
                <TableRow key={r.staff_id} className="border-border" data-testid={`summary-row-${r.staff_id}`}>
                  <TableCell className="font-medium">{r.staff_name}
                    {r.is_home_branch === false && <span className="block text-[11px] text-muted-foreground font-normal" data-testid={`summary-home-note-${r.staff_id}`}>Incentive at {r.home_branch_short}</span>}
                  </TableCell>
                  <TableCell className="text-right">{r.services_count}</TableCell>
                  <TableCell className="text-right" data-testid={`summary-gross-${r.staff_id}`}>{rupee(r.gross_service)}
                    {r.branch_breakdown?.filter((b) => b.gross_service).length > 0 && (
                      <span className="block text-[11px] text-muted-foreground">({r.branch_breakdown.filter((b) => b.gross_service).map((b) => `${b.short_name} ${rupee(b.gross_service)}`).join(", ")})</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">{rupee(r.gst)}</TableCell>
                  <TableCell className="text-right">{rupee(r.net_service)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{r.slab_label}</TableCell>
                  <TableCell className="text-right">{r.slab_percent}%</TableCell>
                  <TableCell className="text-right text-primary">{rupee(r.service_incentive)}</TableCell>
                  <TableCell className="text-right">{rupee(r.product_sales)}</TableCell>
                  <TableCell className="text-right text-primary">{rupee(r.product_incentive)}</TableCell>
                  <TableCell className="text-right text-primary font-medium">{rupee(r.total_incentive)}</TableCell>
                </TableRow>
              ))}
              {data && !data.rows.length && !loading && <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-10">No billing data in this period.</TableCell></TableRow>}
              {loading && <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-10">Loading…</TableCell></TableRow>}
            </TableBody>
            {data && t && !!data.rows.length && (
              <tfoot>
                <TableRow className="border-border bg-secondary/40 font-semibold" data-testid="summary-total-row">
                  <TableCell>TOTAL</TableCell>
                  <TableCell className="text-right">—</TableCell>
                  <TableCell className="text-right">{rupee(t.total_gross_service)}</TableCell>
                  <TableCell className="text-right">{rupee(t.total_gst)}</TableCell>
                  <TableCell className="text-right">{rupee(t.net_business)}</TableCell>
                  <TableCell className="text-right">—</TableCell>
                  <TableCell className="text-right">—</TableCell>
                  <TableCell className="text-right text-primary">{rupee(t.total_service_incentive)}</TableCell>
                  <TableCell className="text-right">{rupee(t.total_product_sales)}</TableCell>
                  <TableCell className="text-right text-primary">{rupee(t.total_product_incentive)}</TableCell>
                  <TableCell className="text-right text-primary" data-testid="summary-total-incentive">{rupee(t.total_incentive)}</TableCell>
                </TableRow>
              </tfoot>
            )}
          </Table>
        </Card>
      </div>
    </div>
  );
}
